import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBed,
  faCar,
  faChevronLeft,
  faChevronRight,
  faMapLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { RESERVATION_TYPES } from "../../constants";
import {
  buildCarSearchUrl,
  buildStaySearchUrl,
  buildTourBuilderUrl,
} from "../../utils/reservationSearchUrls";

const SLIDES = [
  {
    id: RESERVATION_TYPES.STAY,
    image: "/mostar-card-2.jpg",
    icon: faBed,
    gradient:
      "linear-gradient(135deg, rgba(15,118,110,0.82) 0%, rgba(0,0,0,0.55) 100%)",
    accent: "teal",
  },
  {
    id: RESERVATION_TYPES.CAR,
    image: "/road.jpg",
    icon: faCar,
    gradient:
      "linear-gradient(135deg, rgba(49,130,206,0.78) 0%, rgba(0,0,0,0.58) 100%)",
    accent: "blue",
  },
  {
    id: RESERVATION_TYPES.TOUR,
    image: "/tour-bg.jpg",
    icon: faMapLocationDot,
    gradient:
      "linear-gradient(135deg, rgba(128,90,213,0.78) 0%, rgba(0,0,0,0.58) 100%)",
    accent: "purple",
  },
];

const CAROUSEL_DESTINATIONS = {
  [RESERVATION_TYPES.STAY]: () => buildStaySearchUrl("Sarajevo"),
  [RESERVATION_TYPES.CAR]: () => buildCarSearchUrl(),
  [RESERVATION_TYPES.TOUR]: () => buildTourBuilderUrl(),
};

const AUTOPLAY_MS = 4000;
const SWIPE_THRESHOLD_PX = 48;

function ReservationTypesCarousel() {
  const { t, i18n } = useTranslation("home");
  const isRtl = i18n.dir() === "rtl";
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const scheduleAutoplayResume = useCallback(() => {
    if (resumeTimerRef.current) {
      window.clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = window.setTimeout(() => {
      setIsPaused(false);
      resumeTimerRef.current = null;
    }, AUTOPLAY_MS);
  }, []);

  const goTo = useCallback((index) => {
    setActiveIndex((index + SLIDES.length) % SLIDES.length);
  }, []);

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (isPaused) return undefined;

    const timer = window.setInterval(goNext, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [goNext, isPaused]);

  useEffect(
    () => () => {
      if (resumeTimerRef.current) {
        window.clearTimeout(resumeTimerRef.current);
      }
    },
    []
  );

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    setIsPaused(true);
    if (resumeTimerRef.current) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;

    const touch = event.changedTouches[0];
    if (!touch) {
      scheduleAutoplayResume();
      return;
    }

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    if (
      Math.abs(deltaX) < SWIPE_THRESHOLD_PX ||
      Math.abs(deltaX) < Math.abs(deltaY)
    ) {
      scheduleAutoplayResume();
      return;
    }

    const swipedLeft = deltaX < 0;
    if (isRtl) {
      swipedLeft ? goPrev() : goNext();
    } else {
      swipedLeft ? goNext() : goPrev();
    }

    scheduleAutoplayResume();
  };

  const handleTouchCancel = () => {
    touchStartRef.current = null;
    scheduleAutoplayResume();
  };

  const prevIcon = isRtl ? faChevronRight : faChevronLeft;
  const nextIcon = isRtl ? faChevronLeft : faChevronRight;

  const slides = SLIDES.map((slide) => ({
    ...slide,
    to: CAROUSEL_DESTINATIONS[slide.id](),
  }));

  return (
    <Box
      w="full"
      position="relative"
      borderRadius="2xl"
      overflow="hidden"
      shadow="2xl"
      minH={{ base: "320px", md: "420px" }}
      touchAction="pan-y"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      role="region"
      aria-label={t("carousel.ariaLabel")}
      aria-roledescription="carousel"
      aria-live="polite"
    >
      {slides.map((slide, index) => {
        const isActive = index === activeIndex;

        return (
          <Box
            key={slide.id}
            position="absolute"
            inset={0}
            opacity={isActive ? 1 : 0}
            visibility={isActive ? "visible" : "hidden"}
            transition="opacity 0.7s ease-in-out, visibility 0.7s"
            bgImage={`url('${slide.image}')`}
            bgSize="cover"
            bgPosition="center"
            aria-hidden={!isActive}
          >
            <Box
              position="absolute"
              inset={0}
              bg={slide.gradient}
            />
            <Box
              position="absolute"
              inset={0}
              bg="linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)"
            />
            <Flex
              position="relative"
              zIndex={1}
              h="full"
              minH={{ base: "320px", md: "420px" }}
              align="center"
              px={{ base: 6, md: 12 }}
              py={{ base: 8, md: 10 }}
            >
              <VStack
                align={{ base: "center", md: isRtl ? "flex-end" : "flex-start" }}
                spacing={4}
                maxW="2xl"
                mx={{ base: "auto", md: 0 }}
                textAlign={{ base: "center", md: isRtl ? "right" : "left" }}
                color="white"
              >
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="whiteAlpha.250"
                  color="white"
                  fontSize="sm"
                  backdropFilter="blur(8px)"
                  border="1px solid"
                  borderColor="whiteAlpha.400"
                >
                  <FontAwesomeIcon icon={slide.icon} />
                  &nbsp; {t(`carousel.${slide.id}.badge`)}
                </Badge>
                <Heading
                  fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                  fontWeight="800"
                  lineHeight="1.15"
                  textShadow="0 2px 16px rgba(0,0,0,0.35)"
                >
                  {t(`carousel.${slide.id}.title`)}
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  opacity={0.95}
                  maxW="xl"
                  textShadow="0 1px 8px rgba(0,0,0,0.25)"
                >
                  {t(`carousel.${slide.id}.subtitle`)}
                </Text>
                <Button
                  as={RouterLink}
                  to={slide.to}
                  size="lg"
                  colorScheme={slide.accent}
                  mt={2}
                  px={8}
                  _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                  transition="all 0.2s"
                >
                  {t(`carousel.${slide.id}.cta`)}
                </Button>
              </VStack>
            </Flex>
          </Box>
        );
      })}

      <IconButton
        aria-label={t("carousel.prevSlide")}
        icon={<FontAwesomeIcon icon={prevIcon} />}
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        zIndex={2}
        size="sm"
        borderRadius="full"
        bg="blackAlpha.500"
        color="white"
        _hover={{ bg: "blackAlpha.700" }}
        onClick={goPrev}
        left={isRtl ? undefined : { base: 2, md: 4 }}
        right={isRtl ? { base: 2, md: 4 } : undefined}
        display={{ base: "none", sm: "flex" }}
      />
      <IconButton
        aria-label={t("carousel.nextSlide")}
        icon={<FontAwesomeIcon icon={nextIcon} />}
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        zIndex={2}
        size="sm"
        borderRadius="full"
        bg="blackAlpha.500"
        color="white"
        _hover={{ bg: "blackAlpha.700" }}
        onClick={goNext}
        right={isRtl ? undefined : { base: 2, md: 4 }}
        left={isRtl ? { base: 2, md: 4 } : undefined}
        display={{ base: "none", sm: "flex" }}
      />

      <HStack
        position="absolute"
        bottom={{ base: 4, md: 6 }}
        left="50%"
        transform="translateX(-50%)"
        zIndex={2}
        spacing={2}
      >
        {slides.map((slide, index) => (
          <Box
            key={slide.id}
            as="button"
            type="button"
            aria-label={t(`carousel.${slide.id}.badge`)}
            aria-current={index === activeIndex ? "true" : undefined}
            w={index === activeIndex ? "28px" : "10px"}
            h="10px"
            borderRadius="full"
            bg={index === activeIndex ? "white" : "whiteAlpha.600"}
            transition="all 0.3s ease"
            onClick={() => goTo(index)}
            _hover={{ bg: "white" }}
          />
        ))}
      </HStack>
    </Box>
  );
}

export default ReservationTypesCarousel;
